import React, { useEffect, useState } from "react";
import axiosInstance from "../axiosInstance";
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const FEATURE_ROWS = [
  { feature: 'Dashboard & Analytics',           trial: true,        basic: true,          starter: true,        pro: true,              biz: true },
  { feature: 'Dealer Management',               trial: true,        basic: true,          starter: true,        pro: true,              biz: true },
  { feature: 'Stock Inventory',                 trial: true,        basic: true,          starter: true,        pro: true,              biz: true },
  { feature: 'Finance & EMI Loans',             trial: true,        basic: false,         starter: true,        pro: true,              biz: true },
  { feature: 'Collections Management',          trial: true,        basic: false,         starter: true,        pro: true,              biz: true },
  { feature: 'Billing & Invoicing',             trial: true,        basic: false,         starter: true,        pro: true,              biz: true },
  { feature: 'Purchase, Sales & Stock Reports', trial: true,        basic: false,         starter: true,        pro: true,              biz: true },
  { feature: 'Exchange & Trade-In',             trial: true,        basic: false,         starter: false,       pro: true,              biz: true },
  { feature: 'Seized Vehicle Tracking',         trial: true,        basic: false,         starter: false,       pro: true,              biz: true },
  { feature: 'User Management',                 trial: 'Unlimited', basic: true,          starter: false,       pro: 'Up to 3 users',   biz: 'Unlimited' },
  { feature: 'Outstanding & Collection Reports',trial: true,        basic: false,         starter: false,       pro: true,              biz: true },
  { feature: 'Accounts (Business Summary)',     trial: true,        basic: false,         starter: false,       pro: true,              biz: true },
  { feature: 'Outstanding Aging',               trial: true,        basic: false,         starter: false,       pro: true,              biz: true },
  { feature: 'Customer Ledger',                 trial: true,        basic: false,         starter: false,       pro: false,             biz: true },
  { feature: 'Profit & Loss Analysis',          trial: true,        basic: false,         starter: false,       pro: false,             biz: true },
  { feature: 'Support Level',                   trial: 'Email',     basic: 'Email',       starter: 'Email',     pro: 'Priority Email',  biz: 'Phone & Email' },
];

function _cell(val) {
  if (val === true)  return <i className="fas fa-check-circle text-success"></i>;
  if (val === false) return <span className="text-muted">—</span>;
  return <small className="badge badge-light text-dark">{val}</small>;
}

function FeatureComparisonTable() {
  return (
    <div className="row mt-4">
      <div className="col-12">
        <div className="card card-outline card-info">
          <div className="card-header">
            <h3 className="card-title"><i className="fas fa-table mr-2"></i>Feature Comparison</h3>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover table-striped mb-0">
                <thead className="thead-dark">
                  <tr>
                    <th style={{minWidth:200}}>Feature</th>
                    <th className="text-center">Free Trial</th>
                    <th className="text-center">Basic</th>
                    <th className="text-center">Starter</th>
                    <th className="text-center">Professional</th>
                    <th className="text-center">Business</th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_ROWS.map((row, i) => (
                    <tr key={i}>
                      <td className="font-weight-bold small">{row.feature}</td>
                      <td className="text-center">{_cell(row.trial)}</td>
                      <td className="text-center">{_cell(row.basic)}</td>
                      <td className="text-center">{_cell(row.starter)}</td>
                      <td className="text-center">{_cell(row.pro)}</td>
                      <td className="text-center">{_cell(row.biz)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function fmtINR(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0
  }).format(amount);
}

function getDisplayPrice(plan, isAnnual) {
  const annual = parseFloat(plan.price) || 0;
  if (annual === 0) {
    return { label: 'Free', note: `${plan.duration_in_days || 14} days — all features unlocked` };
  }
  const monthly = parseFloat(plan.monthly_price) || Math.round(annual / 12);
  if (isAnnual) {
    return {
      label: `${fmtINR(annual)}/year`,
      note:  `${fmtINR(monthly)}/month equivalent`
    };
  }
  return {
    label: `${fmtINR(monthly)}/month`,
    note:  `${fmtINR(annual)}/year billed annually`
  };
}

export default function PlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isAnnual, setIsAnnual] = useState(true);
  // trialStatuses: { [planId]: { has_enrolled, is_active, is_completed, days_remaining, plan_name } }
  const [trialStatuses, setTrialStatuses] = useState({});
  const [enrollingTrial, setEnrollingTrial] = useState(null);

  const companyId = localStorage.getItem("company_id");

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/plan");
      const HIDDEN_PLANS = ['1 Month Plan', '3 Month Plan', '6 Month Plan', '1 Year Plan'];
      const allPlans = (res.data.data || []).filter(
        p => !HIDDEN_PLANS.some(h => p.name?.startsWith(h))
      );
      const sortedPlans = [...allPlans].sort((a, b) => (a.price || 0) - (b.price || 0));
      setPlans(sortedPlans);

      // Fetch trial statuses if logged in
      if (companyId) {
        try {
          const tr = await axiosInstance.get(`/payment/trial-status/${companyId}`);
          const list = tr.data?.data || [];
          const map = {};
          list.forEach(s => { map[s.plan_id] = s; });
          setTrialStatuses(map);
        } catch (_) { /* non-critical */ }
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
      Swal.fire({
        title: "Error",
        text: "Failed to load subscription plans. Please try again.",
        icon: "error",
        confirmButtonColor: "#007bff"
      });
    } finally {
      setLoading(false);
    }
  };

  const validatePaymentData = (plan) => {
    if (!companyId) {
      Swal.fire({
        title: "Authentication Required",
        text: "Please log in to subscribe to a plan.",
        icon: "warning",
        confirmButtonColor: "#007bff"
      });
      return false;
    }
    if (!plan || !plan.id || !plan.price) {
      Swal.fire({
        title: "Invalid Plan",
        text: "Selected plan data is invalid. Please try again.",
        icon: "error",
        confirmButtonColor: "#007bff"
      });
      return false;
    }
    return true;
  };

  const handleSubscribe = async (plan) => {
    if (!validatePaymentData(plan)) return;

    const annual       = parseFloat(plan.price) || 0;
    const monthly      = parseFloat(plan.monthly_price) || Math.round(annual / 12);
    const amount       = isAnnual ? annual : monthly;
    const billingCycle = isAnnual ? 'annual' : 'monthly';

    try {
      setProcessingPayment(true);
      setSelectedPlan(plan.id);

      Swal.fire({
        title: "Processing...",
        text: "Setting up your payment...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        willOpen: () => { Swal.showLoading(); }
      });

      const res = await axiosInstance.post("/payment", {
        company_id:    companyId,
        plan_id:       plan.id,
        amount:        amount,
        billing_cycle: billingCycle,
      });

      const order = res.data.data || res.data;

      if (!order || !order.razorpay_order_id) {
        throw new Error(`Missing razorpay_order_id in response. Got: ${JSON.stringify(order)}`);
      }

      Swal.close();

      const userName   = localStorage.getItem('name') || '';
      const userEmail  = localStorage.getItem('email') || '';
      const companyName = localStorage.getItem('companyname') || 'Your Company';

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY || "rzp_test_6JDIxu0tbHEIc6",
        amount: amount * 100,
        currency: "INR",
        name: companyName,
        description: `${plan.name} — ${isAnnual ? 'Annual' : 'Monthly'} Plan`,
        order_id: order.razorpay_order_id,
        image: "/logo192.png",
        handler: async function (response) {
          await handlePaymentSuccess(response, plan, amount, billingCycle);
        },
        prefill: {
          name: userName,
          email: userEmail,
          contact: ""
        },
        notes: {
          company_id:    companyId,
          plan_id:       plan.id,
          plan_name:     plan.name,
          billing_cycle: billingCycle,
          amount:        amount
        },
        theme: { color: "#007bff" },
        modal: {
          ondismiss: function () {
            setProcessingPayment(false);
            setSelectedPlan(null);
            Swal.fire({
              title: "Payment Cancelled",
              text: "Your payment was cancelled. You can try again anytime.",
              icon: "info",
              confirmButtonColor: "#007bff"
            });
          }
        },
        retry: { enabled: true, max_count: 3 }
      };

      if (!window.Razorpay) {
        throw new Error("Razorpay SDK not loaded. Please refresh the page and try again.");
      }

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        handlePaymentFailure(response, plan);
      });
      rzp.open();

    } catch (err) {
      console.error("Payment initiation error:", err);
      setProcessingPayment(false);
      setSelectedPlan(null);

      let errorMessage = "Failed to initiate payment. Please try again.";
      if (err.response) {
        errorMessage = err.response.data?.message || `Server Error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = "Network error. Please check your connection.";
      } else {
        errorMessage = err.message || errorMessage;
      }

      Swal.fire({
        title: "Payment Error",
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#007bff"
      });
    }
  };

  const handlePaymentSuccess = async (response, plan, amount, billingCycle) => {
    try {
      Swal.fire({
        title: "Verifying Payment...",
        text: "Please wait while we confirm your payment.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        willOpen: () => { Swal.showLoading(); }
      });

      await axiosInstance.post("/payment/record", {
        company_id:           companyId,
        plan_id:              plan.id,
        amount:               amount,
        billing_cycle:        billingCycle,
        razorpay_payment_id:  response.razorpay_payment_id,
        razorpay_order_id:    response.razorpay_order_id,
        razorpay_signature:   response.razorpay_signature
      });

      Swal.fire({
        title: "Payment Successful!",
        text: `You have successfully subscribed to ${plan.name}. Your subscription is now active.`,
        icon: "success",
        confirmButtonColor: "#28a745",
        confirmButtonText: "Great!"
      });

    } catch (err) {
      console.error("Payment recording error:", err);
      Swal.fire({
        title: "Payment Verification Failed",
        text: "Your payment was processed, but we couldn't verify it. Please contact support.",
        icon: "warning",
        confirmButtonColor: "#ffc107"
      });
    } finally {
      setProcessingPayment(false);
      setSelectedPlan(null);
    }
  };

  const handleTrialEnroll = async (plan) => {
    if (!companyId) {
      Swal.fire({ title: 'Login Required', text: 'Please log in to start a trial.', icon: 'warning', confirmButtonColor: '#007bff' });
      return;
    }
    setEnrollingTrial(plan.id);
    try {
      await axiosInstance.post(`/payment/enroll-trial/${companyId}`, { plan_id: plan.id });
      Swal.fire({
        title: 'Trial Started!',
        text: `Your ${plan.name} is now active. Enjoy full access for ${plan.duration_in_days || 14} days!`,
        icon: 'success',
        confirmButtonColor: '#28a745',
      });
      // Refresh trial status
      const tr = await axiosInstance.get(`/payment/trial-status/${companyId}`);
      const list = tr.data?.data || [];
      const map = {};
      list.forEach(s => { map[s.plan_id] = s; });
      setTrialStatuses(map);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to start trial. Please try again.';
      Swal.fire({ title: 'Unable to Start Trial', text: msg, icon: 'error', confirmButtonColor: '#dc3545' });
    } finally {
      setEnrollingTrial(null);
    }
  };

  const handlePaymentFailure = (response, plan) => {
    console.error("Payment failed:", response);
    setProcessingPayment(false);
    setSelectedPlan(null);

    const errorMessage = response.error?.description || "Payment failed. Please try again.";

    Swal.fire({
      title: "Payment Failed",
      text: errorMessage,
      icon: "error",
      confirmButtonColor: "#dc3545",
      showCancelButton: true,
      confirmButtonText: "Try Again",
      cancelButtonText: "Cancel"
    }).then((result) => {
      if (result.isConfirmed) {
        handleSubscribe(plan);
      }
    });
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">Subscription Plans</h1>
              </div>
            </div>
          </div>
        </div>
        <section className="content">
          <div className="container-fluid">
            <div className="row justify-content-center">
              <div className="col-md-6 text-center">
                <div className="card">
                  <div className="card-body">
                    <div className="spinner-border text-primary mb-3" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="text-muted">Loading subscription plans...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">Subscription Plans</h1>
              </div>
            </div>
          </div>
        </div>
        <section className="content">
          <div className="container-fluid">
            <div className="row justify-content-center">
              <div className="col-md-6">
                <div className="card card-warning">
                  <div className="card-header">
                    <h3 className="card-title">No Plans Available</h3>
                  </div>
                  <div className="card-body text-center">
                    <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                    <p>Sorry, there are no subscription plans available at the moment.</p>
                    <button onClick={fetchPlans} className="btn btn-primary">
                      <i className="fas fa-sync-alt mr-2"></i>
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      {/* Content Header */}
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0">
                <i className="fas fa-crown text-warning mr-2"></i>
                Choose Your Plan
              </h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">Dashboard</li>
                <li className="breadcrumb-item active">Subscription Plans</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <section className="content">
        <div className="container-fluid">

          {/* Billing Toggle */}
          <div className="row mb-3">
            <div className="col-12 text-center">
              <div className="btn-group" role="group" aria-label="Billing cycle">
                <button
                  type="button"
                  className={`btn btn-lg ${!isAnnual ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setIsAnnual(false)}
                >
                  <i className="fas fa-calendar mr-2"></i>Monthly
                </button>
                <button
                  type="button"
                  className={`btn btn-lg ${isAnnual ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setIsAnnual(true)}
                >
                  <i className="fas fa-calendar-alt mr-2"></i>Annual
                  <span className="badge badge-success ml-2">SAVE</span>
                </button>
              </div>
              <p className="text-muted mt-2 mb-0 small">
                {isAnnual
                  ? 'Pay once for the full year and save more.'
                  : 'Pay month by month — flexible and cancel anytime.'}
              </p>
            </div>
          </div>

          {/* Info callout */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="callout callout-info">
                <h5>
                  <i className="fas fa-tag mr-2"></i>
                  14-Day Free Trial Included
                </h5>
                Start with a free trial — no credit card required. Upgrade anytime.
              </div>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="row">
            {plans.map((plan) => {
              const isPopular = !!plan.popular;
              const { label, note } = getDisplayPrice(plan, isAnnual);

              return (
                <div key={plan.id} className="col-lg-3 col-md-6 mb-4">
                  <div
                    className={`card elevation-4 ${isPopular ? 'card-primary card-outline' : ''}`}
                    style={{
                      borderTop: isPopular ? '4px solid #007bff' : '2px solid #dee2e6',
                      transition: 'transform 0.3s, box-shadow 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-10px)';
                      e.currentTarget.style.boxShadow = '0 1rem 3rem rgba(0,0,0,.175)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    {/* Popular Badge */}
                    {isPopular && (
                      <div className="ribbon-wrapper ribbon-lg">
                        <div className="ribbon bg-primary">POPULAR</div>
                      </div>
                    )}

                    {/* Card Header */}
                    <div className={`card-header text-center ${isPopular ? 'bg-primary' : 'bg-light'}`}>
                      <h3 className={`card-title font-weight-bold text-lg mb-0 ${isPopular ? 'text-white' : ''}`}>
                        {plan.name}
                      </h3>
                      {isPopular && <small className="text-white">⭐ Most Popular</small>}
                    </div>

                    {/* Card Body */}
                    <div className="card-body text-center" style={{ minHeight: '340px' }}>
                      {/* Pricing */}
                      <div className="mb-3">
                        <h2 className="mb-0 font-weight-bold text-primary" style={{fontSize:'1.6rem', whiteSpace:'nowrap'}}>
                          {label}
                        </h2>
                        <p className="text-muted small mb-0">{note}</p>
                      </div>

                      {/* Features */}
                      <ul className="list-unstyled text-left mb-0">
                        {(plan.features || [
                          'Full access to all features',
                          '24/7 Priority support',
                          'Free updates included',
                        ]).map((feature, index) => (
                          <li key={index} className="mb-2">
                            <i className="fas fa-check-circle text-success mr-2"></i>
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Card Footer */}
                    <div className="card-footer text-center bg-white">
                      {/* Free / trial plans */}
                      {parseFloat(plan.price) === 0 ? (() => {
                        const ts = trialStatuses[plan.id];
                        if (ts?.is_active) {
                          return (
                            <div>
                              <span className="badge badge-success badge-pill px-3 py-2" style={{fontSize:'0.85rem'}}>
                                <i className="fas fa-hourglass-half mr-1"></i>
                                {ts.days_remaining} day{ts.days_remaining !== 1 ? 's' : ''} remaining
                              </span>
                              <small className="text-muted d-block mt-2">Trial is currently active</small>
                            </div>
                          );
                        }
                        if (ts?.is_completed) {
                          return (
                            <div>
                              <span className="badge badge-secondary badge-pill px-3 py-2" style={{fontSize:'0.85rem'}}>
                                <i className="fas fa-check mr-1"></i>
                                Trial Completed
                              </span>
                              <small className="text-muted d-block mt-2">Already used — upgrade to a paid plan</small>
                            </div>
                          );
                        }
                        // Not yet enrolled
                        return (
                          <button
                            className="btn btn-lg btn-block btn-success elevation-2"
                            onClick={() => handleTrialEnroll(plan)}
                            disabled={enrollingTrial === plan.id || !companyId}
                            style={{ fontWeight: 'bold' }}
                          >
                            {enrollingTrial === plan.id ? (
                              <><span className="spinner-border spinner-border-sm mr-2"></span>Starting...</>
                            ) : (
                              <><i className="fas fa-play-circle mr-2"></i>Start Free Trial</>
                            )}
                          </button>
                        );
                      })() : (
                        /* Paid plans */
                        <button
                          className={`btn btn-lg btn-block ${isPopular ? 'btn-primary' : 'btn-outline-primary'} elevation-2`}
                          onClick={() => handleSubscribe(plan)}
                          disabled={processingPayment || !companyId}
                          style={{ fontWeight: 'bold', transition: 'all 0.3s' }}
                        >
                          {selectedPlan === plan.id ? (
                            <><span className="spinner-border spinner-border-sm mr-2"></span>Processing...</>
                          ) : (
                            <><i className="fas fa-rocket mr-2"></i>Pay Now</>
                          )}
                        </button>
                      )}
                      {!companyId && (
                        <small className="text-danger d-block mt-2">
                          <i className="fas fa-exclamation-circle mr-1"></i>
                          Please log in to subscribe
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <FeatureComparisonTable />

          {/* What every plan includes */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="card card-primary card-outline">
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-star mr-2"></i>
                    All Plans Include
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-3 col-sm-6 mb-3">
                      <div className="info-box bg-gradient-info">
                        <span className="info-box-icon"><i className="fas fa-sync-alt"></i></span>
                        <div className="info-box-content">
                          <span className="info-box-text">Auto Updates</span>
                          <span className="info-box-number">Always Latest</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 col-sm-6 mb-3">
                      <div className="info-box bg-gradient-success">
                        <span className="info-box-icon"><i className="fas fa-database"></i></span>
                        <div className="info-box-content">
                          <span className="info-box-text">Cloud Storage</span>
                          <span className="info-box-number">Unlimited Storage</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 col-sm-6 mb-3">
                      <div className="info-box bg-gradient-warning">
                        <span className="info-box-icon"><i className="fas fa-headset"></i></span>
                        <div className="info-box-content">
                          <span className="info-box-text">Support</span>
                          <span className="info-box-number">24/7 Available</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 col-sm-6 mb-3">
                      <div className="info-box bg-gradient-danger">
                        <span className="info-box-icon"><i className="fas fa-shield-alt"></i></span>
                        <div className="info-box-content">
                          <span className="info-box-text">Security</span>
                          <span className="info-box-number">SSL Encrypted</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card card-secondary">
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-shield-alt mr-2"></i>
                    Why Choose Us?
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4 text-center mb-3">
                      <div className="p-3">
                        <i className="fas fa-clock fa-3x text-info mb-3"></i>
                        <h5 className="font-weight-bold">24/7 Support</h5>
                        <p className="text-muted">Round the clock customer support for all your needs</p>
                      </div>
                    </div>
                    <div className="col-md-4 text-center mb-3">
                      <div className="p-3">
                        <i className="fas fa-undo fa-3x text-success mb-3"></i>
                        <h5 className="font-weight-bold">Money Back Guarantee</h5>
                        <p className="text-muted">30-day money back guarantee if you're not satisfied</p>
                      </div>
                    </div>
                    <div className="col-md-4 text-center mb-3">
                      <div className="p-3">
                        <i className="fas fa-lock fa-3x text-warning mb-3"></i>
                        <h5 className="font-weight-bold">Secure Payments</h5>
                        <p className="text-muted">All payments are secured with industry standard encryption</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
